import { useState, useCallback } from 'react';

const DEFAULT_TVA_RATES = [0, 5.5, 10, 20];
const TVA_RATES_KEY = 'tva_rates_custom';
const TVA_PCG_KEY = 'tva_pcg_comptes';

// Map: taux (as string key) -> comma-separated compte PCG list
export type TvaPcgMap = Record<string, string>;

function loadRates(): number[] {
  try {
    const stored = localStorage.getItem(TVA_RATES_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.every((r) => typeof r === 'number')) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return DEFAULT_TVA_RATES;
}

function loadPcgMap(): TvaPcgMap {
  try {
    const stored = localStorage.getItem(TVA_PCG_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as TvaPcgMap;
      }
    }
  } catch {
    // ignore
  }
  return {};
}

export interface UseTvaRatesReturn {
  rates: number[];
  pcgMap: TvaPcgMap;
  saveRates: (newRates: number[]) => void;
  savePcgMap: (map: TvaPcgMap) => void;
  resetRates: () => void;
}

export function useTvaRates(): UseTvaRatesReturn {
  const [rates, setRates] = useState<number[]>(loadRates);
  const [pcgMap, setPcgMap] = useState<TvaPcgMap>(loadPcgMap);

  const saveRates = useCallback((newRates: number[]) => {
    const sorted = [...new Set(newRates.map((r) => Number(r)))].sort((a, b) => a - b);
    setRates(sorted);
    localStorage.setItem(TVA_RATES_KEY, JSON.stringify(sorted));
  }, []);

  const savePcgMap = useCallback((map: TvaPcgMap) => {
    setPcgMap(map);
    localStorage.setItem(TVA_PCG_KEY, JSON.stringify(map));
  }, []);

  const resetRates = useCallback(() => {
    setRates(DEFAULT_TVA_RATES);
    setPcgMap({});
    localStorage.removeItem(TVA_RATES_KEY);
    localStorage.removeItem(TVA_PCG_KEY);
  }, []);

  return { rates, pcgMap, saveRates, savePcgMap, resetRates };
}
